import { ArrowRight, Zap, XCircle, CheckCircle2, Building2, Users, BarChart3 } from "lucide-react";
import { useState, useEffect } from "react";
import { useFlexOfficeCTA } from "./useFlexOfficeCTA";
import data from "../data/flexOfficeData.json"; 
const ICON_MAP = { Building2, Users, BarChart3 };

const useBreakpoint = () => {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return { isMobile: w < 640, isTablet: w >= 640 && w < 1024 };
};

const FlexOfficeProblemSolution = () => {
  const cta = useFlexOfficeCTA();
  const { isMobile, isTablet } = useBreakpoint();
  const d = data.problemSolution;

  return (
    <section style={{ background: "#f8fafc", padding: isMobile ? "60px 20px" : isTablet ? "72px 32px" : "96px 0", overflow: "hidden" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: isMobile ? "0" : isTablet ? "0" : "0 40px" }}>
        <div style={{ textAlign: "center", marginBottom: isMobile ? 40 : 64 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#eff6ff", border: "1px solid #bfdbfe", padding: "8px 18px", borderRadius: 100, marginBottom: 20 }}>
            <Zap style={{ width: 14, height: 14, color: "#1d4ed8" }} />
            <span style={{ color: "#1d4ed8", fontSize: 13, fontWeight: 700, letterSpacing: "0.05em" }}>{d.badge}</span>
          </div>
          <h2 style={{ fontSize: isMobile ? 28 : isTablet ? 38 : 48, fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.1, color: "#0f172a" }}>
            {d.headline} <span style={{ color: "#1d4ed8" }}>{d.headlineAccent}</span>
          </h2>
          <p style={{ fontSize: isMobile ? 15 : 18, color: "#64748b", maxWidth: 560, margin: "16px auto 0", lineHeight: 1.7 }}>{d.description}</p>
        </div>

        {/* Problem / Solution cards */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? 16 : 24 }}>
          {/* Without */}
          <div style={{ background: "white", border: "1px solid #fee2e2", borderRadius: 20, padding: isMobile ? 24 : 40 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
              <div style={{ width: 40, height: 40, background: "#fef2f2", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <XCircle style={{ width: 20, height: 20, color: "#ef4444" }} />
              </div>
              <h3 style={{ fontSize: isMobile ? 15 : 20, fontWeight: 800, color: "#0f172a" }}>Without Krumago FlexOffice</h3>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {d.problems.map((p, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <XCircle style={{ width: 18, height: 18, color: "#fca5a5", flexShrink: 0, marginTop: 1 }} />
                  <span style={{ color: "#64748b", fontSize: isMobile ? 14 : 15 }}>{p}</span>
                </div>
              ))}
            </div>
          </div>

          {/* With */}
          <div style={{ background: "white", border: "1px solid #d1fae5", borderRadius: 20, padding: isMobile ? 24 : 40 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
              <div style={{ width: 40, height: 40, background: "#f0fdf4", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <CheckCircle2 style={{ width: 20, height: 20, color: "#22c55e" }} />
              </div>
              <h3 style={{ fontSize: isMobile ? 15 : 20, fontWeight: 800, color: "#0f172a" }}>With Krumago FlexOffice</h3>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {d.solutions.map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <CheckCircle2 style={{ width: 18, height: 18, color: "#22c55e", flexShrink: 0, marginTop: 1 }} />
                  <span style={{ color: "#0f172a", fontSize: isMobile ? 14 : 15, fontWeight: 500 }}>{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "repeat(3, 1fr)", gap: isMobile ? 12 : 20, marginTop: 24 }}>
          {d.benefits.map((b, i) => {
            const IconComp = ICON_MAP[b.icon];
            return (
              <div key={i} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 16, padding: isMobile ? 20 : 28, textAlign: "center", transition: "all 0.3s", cursor: "default" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 20px 50px rgba(29,78,216,0.12)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}>
                <div style={{ width: 52, height: 52, background: "#eff6ff", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", color: "#1d4ed8" }}>
                  {IconComp && <IconComp style={{ width: 24, height: 24 }} />}
                </div>
                <div style={{ fontWeight: 800, fontSize: isMobile ? 15 : 17, color: "#0f172a", marginBottom: 8 }}>{b.title}</div>
                <div style={{ color: "#64748b", fontSize: isMobile ? 13 : 14, lineHeight: 1.6 }}>{b.desc}</div>
              </div>
            );
          })}
        </div>

        {/* ── CTA: "Get Started with FlexOffice" ── */}
        <div style={{ textAlign: "center", marginTop: isMobile ? 32 : 48 }}>
          <button
            onClick={cta.getStarted}
            style={{ background: "#1d4ed8", color: "white", border: "none", padding: isMobile ? "13px 28px" : "15px 36px", borderRadius: 12, fontSize: isMobile ? 14 : 15, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10, transition: "all 0.25s", width: isMobile ? "100%" : "auto", justifyContent: "center" }}
            onMouseEnter={e => { e.currentTarget.style.background = "#1e3a8a"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 25px rgba(29,78,216,0.35)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#1d4ed8"; e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}>
            {d.cta} <ArrowRight style={{ width: 17, height: 17 }} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default FlexOfficeProblemSolution;